import { useState, useEffect } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid"
import uescaLogo from 'media/logos/uesca_logo.png'
import eightyTwentyLogo from 'media/logos/8020_logo.png'

export default function OrganizationBillingConfiguration({
    billingData = null
}) {
    const [pricingTiers, setPricingTiers] = useState(null);
    const [totalBillableSeats, setTotalBillableSeats] = useState(null);
    const [basePricePerAthlete, setBasePricePerAthlete] = useState(null);
    const [activeTier, setActiveTier] = useState(null);
    const [expanded, setExpanded] = useState(false);

    const GetActiveTier = (pricingTiers, totalBillableSeats) => {
        if (!pricingTiers || pricingTiers.length === 0) {
            return null
        }
    
        for (const tier of pricingTiers) {
            if (
                tier.billableSeatLowerBound <= totalBillableSeats &&
                totalBillableSeats <= tier.billableSeatUpperBound
            ) {
                return tier
            }
        }
    
        return null
    }

    const IsTierActive = (tier) => {
        if (totalBillableSeats === null) return false
        return tier.billableSeatLowerBound <= totalBillableSeats && totalBillableSeats <= tier.billableSeatUpperBound
    }

    useEffect(() => {
        setPricingTiers(billingData?.organizationBillingInfo?.pricingTiersJSON || []);
        setTotalBillableSeats(billingData?.itemizedReceipt?.billableAthleteCount || 0);
        setBasePricePerAthlete(billingData?.itemizedReceipt?.basePricePerAthlete || 0);
        setActiveTier(GetActiveTier(billingData?.organizationBillingInfo?.pricingTiersJSON, billingData?.itemizedReceipt?.billableAthleteCount));
    }, [billingData]);

    if (
        pricingTiers === null ||
        activeTier === null ||
        totalBillableSeats === null ||
        basePricePerAthlete === null
    ) {
        return null;
    }

    return (
        <>
            <div 
                className='p-4 flex items-center cursor-pointer'
                onClick={() => setExpanded(!expanded)}
            >
                <div>Seat-Based Pricing</div>

                <div className="flex-1" />

                <div className="text-sm text-gray-500 pr-4">
                    {totalBillableSeats} billable athlete{totalBillableSeats > 1 ? 's' : ''} at ${basePricePerAthlete.toFixed(2)} per athlete
                </div>

                <div className="transition-transform duration-300 ease-in-out">
                    {expanded === false && <ChevronDownIcon className="h-6 w-6 text-gray-500" />}
                    {expanded === true && <ChevronUpIcon className="h-6 w-6 text-gray-500" />}
                </div>
            </div>

            <div
                className={`
                    transition-all duration-300 ease-in-out overflow-hidden divide-y divide-gray-200
                    ${expanded ? 'opacity-100' : 'max-h-0 opacity-0'}
                `}
            >
                {billingData?.organizationBillingInfo?.pricingTiersDiscountJSON !== null &&
                    <>
                        {billingData?.organizationBillingInfo?.pricingTiersDiscountJSON.key === 'partner' && 
                            <div className='p-4 flex items-center gap-x-1 bg-gray-50'>
                                <div className="text-sm flex-1">Partnership Discount</div>

                                <div className="text-sm text-gray-500">
                                    {billingData?.organizationBillingInfo?.pricingTiersDiscountJSON.percentage * 100}% off all tiers
                                </div>   
                            </div>
                        }

                        {billingData?.organizationBillingInfo?.pricingTiersDiscountJSON.key === 'uesca' && 
                            <div className='p-4 flex items-center gap-x-2 bg-gray-50'>
                                <img 
                                    src={uescaLogo} 
                                    alt='UESCA Logo' 
                                    className="h-6 object-cover" 
                                />

                                <div className="text-sm flex-1">Partnership Discount</div>

                                <div className="text-sm text-gray-500">
                                    {billingData?.organizationBillingInfo?.pricingTiersDiscountJSON.percentage * 100}% off all tiers
                                </div>
                            </div>
                        } 

                        {billingData?.organizationBillingInfo?.pricingTiersDiscountJSON.key === '8020' && 
                            <div className='p-4 flex items-center gap-x-2 bg-gray-50'>
                                <img 
                                    src={eightyTwentyLogo} 
                                    alt='8020 Logo' 
                                    className="h-6 object-cover" 
                                />

                                <div className="text-sm flex-1">Partnership Discount</div>

                                <div className="text-sm text-gray-500">
                                    {billingData?.organizationBillingInfo?.pricingTiersDiscountJSON.percentage * 100}% off all tiers
                                </div>
                            </div>
                        } 
                    </>
                }

                {pricingTiers.map((tier, index) => (
                    <div 
                        key={index}
                        className={`
                            p-4 flex items-center gap-x-2
                            ${IsTierActive(tier) ? 'bg-orange-50' : 'bg-gray-50'}
                        `}
                    >
                        <div className="text-sm">{tier.tierName}</div>

                        {tier.billableSeatUpperBound !== 9999 &&
                            <span className="text-sm text-gray-500">{tier.billableSeatLowerBound}-{tier.billableSeatUpperBound} athletes</span>
                        }

                        {tier.billableSeatUpperBound === 9999 &&
                            <span className="text-sm text-gray-500">{tier.billableSeatLowerBound}+ athletes</span>
                        }

                        <div className="flex-1" />

                        {IsTierActive(tier) &&
                            <div className="mr-2 px-4 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium whitespace-nowrap">
                                Active
                            </div>
                        }

                        <div className="text-sm text-gray-500">${tier.pricePerSeat.toFixed(2)} per athlete</div>
                    </div>
                ))}
            </div>
        </>
    )
}